/**
 * Teen Patti 20-20 — the SERVER table clock (Phase D, plan §5.3/§6.0/§6.3, contract C5).
 *
 * This is the same seam as `LocalTableClock`, wired to the real table: the round comes from
 * `GET /api/teen_patti/state`, both hands come from the worker's decision, and every rupee on
 * screen is one the server told us about. `Page.tsx` swaps ONE import line and changes nothing
 * else — every phase it renders is still derived from `round()`.
 *
 * It is deliberately a near-copy of `andar_bahar/serverTableClock.ts`, which was written for
 * Phase C and proven with real rupees in a browser on 2026-09-06. Where the two differ, the
 * difference is a Teen Patti rule, not a second opinion about how to talk to the service.
 *
 * ── THE FOUR RULES THIS FILE OBEYS ────────────────────────────────────────────────────────
 * 1. NEVER animate a result the server did not return. There is no local RNG here, no
 *    `candidates`, no `deal`. If the worker has not written the deal by `resolvesAt + 1 s`
 *    the clock STALLS in `locked` (the strip reads BETS CLOSED — DEALING, and `syncing()` is
 *    true for any UI that wants to say SYNCING) until the hands arrive. A card invented on
 *    the client is a card the wallet will not honour.
 * 2. Every wallet figure is the server's. `/bet`, `/unbet` and `/state` all return the
 *    authoritative balance; the practice `Wallet` the Page owns is DRIVEN to that number
 *    (`credit`/`debit` by the difference) and `setWalletBalance()` keeps the rest of the app
 *    in step. The only local arithmetic is the optimistic hold between a tap and its ack.
 * 3. Time is the server's. Round stamps arrive as server epoch ms and are converted ONCE into
 *    the `performance.now()` domain the shipped Page subtracts from (`toPerf`), so
 *    `serverNow()` can keep returning `performance.now()` and no Page line changes.
 * 4. A tap is optimistic but never a lie. The chip appears immediately with `pending:true`;
 *    the ack replaces it with the real bet id and the real balance; a refusal removes it and
 *    puts the money back. Nothing is ever shown as settled that the server has not settled.
 *
 * ── THE REVEAL, AND WHY ITS SHAPE IS THE SERVER'S ─────────────────────────────────────────
 * `_tables/model.js` `REVEALABLE` includes `locked` for the card games, because the CLIENT
 * draws the animation and cannot start it without both hands. So `/state` ships the whole deal
 * the moment betting closes — which is exactly what `resultView` in `teen_patti/adapter.js`
 * whitelists (winner, a[3], b[3], ra/rb as {cat,name}, x). Nothing is hidden at that point: the
 * deal IS the result. What must never ship is the CANDIDATE SET or the decision label `mode`,
 * and neither has a path out of `roundView`.
 *
 * The cadence is `paces.js`, not this file's opinion: `animationMs('teen_patti')` is
 * `TP_CARDS × TP_FLIP_MS + TP_RESULT_MS` = 6 × 1100 + 1500 = 8100 ms, and the worker stamps
 * `settles_at = resolves_at + 8100`. So the six flips own the first 6600 ms and the result hold
 * owns the last 1500 — which is why the sixth card lands at `resolvesAt + 5500` and the stamp
 * only comes down 1100 ms later, when that card's own beat is spent. The practice clock ran the
 * result burst straight off the sixth card because it had no `settles_at` to hit; here we hit
 * the server's, to the millisecond, so the table cannot finish a round the server has not.
 *
 * ── WHY THE DISPLAYED ROUND IS NOT ALWAYS `state.round` ───────────────────────────────────
 * The server's `round` is the oldest NON-TERMINAL row: the moment round N settles it stops
 * being `round` and becomes `lastResult`, while round N+1 (inserted `pending` at decision
 * time, opening 3 s later) becomes `round`. The player, meanwhile, is still looking at N's
 * winning hand. So the clock displays "the newest round whose window has opened", which is N
 * until N+1 actually opens — exactly the local clock's behaviour, and the reason `myOutcome()`
 * survives the settle instead of blinking to YOU SAT OUT.
 *
 * ── IDLE TABLES LOOK LIKE THIS ────────────────────────────────────────────────────────────
 * §5.2: a table with no real bet for 15 minutes writes rounds BORN SETTLED — `worker.js`
 * `decideRound` short-circuits to `status='settled'` with no `result` and null
 * `resolves_at`/`settles_at`/`next_opens_at`. Teen Patti has been running that way since the
 * service came up (5,955 settled rounds, zero results, zero bets — checked 2026-09-06), so it
 * is the state a player will actually arrive into, not an edge case. This clock therefore
 * treats "locked with no deal" as a first-class phase rather than an error: the strip says
 * BETS CLOSED — DEALING for the couple of seconds until the next round opens, and no card is
 * ever drawn to fill the gap. The moment a real chip lands the table stops being idle and the
 * worker decides the round properly.
 */

import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { setWalletBalance, refreshWallet } from '@baaligames/shared-ui/lib/wallet';
import { type Card, type Rank, type Suit, RANKS, SUITS } from '../_cards/deck';
import { type Side, type Category, type HandRank, CATEGORY_NAME, betReturn } from './engine';
import { mix } from '../aviator/prng';
import { createClockSync, type ClockSync } from '../_shared/serverClock';
import { createPoller, type Poller } from '../_shared/poller';
import {
  LocalTableClock,
  FLIP_MS, RESULT_MS,
  type TableClock, type Round, type Bet, type Wallet, type RoundStatus,
} from './tableClock';

// Re-exported so `Page.tsx`'s single import line can name everything it used to import from
// `./tableClock` (the types, the flip cadence) plus this implementation.
export {
  LocalTableClock, WINDOW_MS, ARM_MS, FLIP_MS, RESULT_MS, INTERMISSION_MS,
} from './tableClock';
export type { TableClock, Round, Bet, Wallet, RoundStatus } from './tableClock';

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Wire types — exactly what `_tables/model.js` serialises (its whitelist, not a DB row) after
 * `teen_patti/adapter.js` `resultView` has narrowed the result. Confirmed against
 * `model.js roundView` / `getState` and `adapter.js resultView`, not guessed.
 * ════════════════════════════════════════════════════════════════════════════════════════ */

type SrvStatus = 'pending' | 'betting' | 'locked' | 'resolving' | 'settling' | 'settled' | 'void';
type SrvCard = { rank: string; suit: string };
/** `adapter.js rank()` — the category number and its printable name, nothing else. */
type SrvRank = { cat: number; name: string } | null;

type SrvResult = {
  winner: Side | 'push';
  a: SrvCard[]; b: SrvCard[];
  ra: SrvRank; rb: SrvRank;
  /** 1.98 on a decided side, 1.00 on a push — the TOTAL RETURN, per the X-doctrine. */
  x: number | null;
} | null;

type SrvOutcome = {
  staked: number; returned: number; net: number;
  perBet?: { betId: number; market: string; amount: number; payout: number; cancelled: boolean }[];
} | null;

type SrvRound = {
  id: number; no: number; tableId: number; status: SrvStatus;
  opensAt: number | null; locksAt: number | null; resolvesAt: number | null;
  settlesAt: number | null; nextOpensAt: number | null; settledAt: number | null;
  result: SrvResult;
  /** only on `state.round` — the live pot and headcount (§5.3 P9: never a per-side split) */
  pot?: number; bettors?: number;
  myOutcome?: SrvOutcome;
};

type SrvMyBet = {
  betId: number; roundId: number; roundNo: number; market: string;
  amount: number; payout: number;
};

type TpState = {
  serverNow: number;
  wallet: number;
  enabled: boolean;
  bet: { min: number; max: number; presets: number[]; maxPerRound: number };
  table: { id: number; name: string; pace: string; windowMs: number; intermissionMs: number; status: string; fakeSeed: number; sortOrder: number };
  round: SrvRound | null;
  nextRound: SrvRound | null;
  lastResult: SrvRound | null;
  history: SrvRound[];
  myBets: SrvMyBet[];
};

type BetAck = {
  betId: number; roundId: number; roundNo: number; tableId: number;
  market: string; amount: number; roundTotal: number; wallet: number; duplicate?: boolean; cancelled?: boolean;
};
type UnbetAck = { betId: number; roundId: number; refunded: number; wallet: number; duplicate?: boolean };

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Constants that MUST agree with the server
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** `paces.js` PACES.teen_patti.lock — `/bet` refuses inside this cushion before `locks_at`. */
const LOCK_MS = 500;
/** …plus the plan's 150 ms client margin (§5.3 "greys BET at locks_at − lock_ms − 150ms"). */
const LOCK_CUSHION_MS = LOCK_MS + 150;
/** `paces.js` TP_CARDS — three cards each, dealt A, B, A, B, A, B. */
const CARDS = 6;
/** How long we wait past `resolvesAt` for the worker's deal before saying we are out of sync. */
const SYNCING_AFTER_MS = 1000;
/** The local re-derive tick. The Page re-renders only when something it shows changed. */
const TICK_MS = 100;

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Pure helpers
 * ════════════════════════════════════════════════════════════════════════════════════════ */

const RANK_SET = new Set<string>(RANKS as readonly string[]);
const SUIT_SET = new Set<string>(SUITS as readonly string[]);

/** A wire card → a `Card`, or null. Nothing unvalidated reaches the renderer. */
export function toCard(c: SrvCard | null | undefined): Card | null {
  if (!c || typeof c !== 'object') return null;
  const rank = String(c.rank); const suit = String(c.suit);
  if (!RANK_SET.has(rank) || !SUIT_SET.has(suit)) return null;
  return { rank: rank as Rank, suit: suit as Suit };
}

/**
 * A wire hand rank → the Page's `HandRank`.
 *
 * The category and its printable name are the SERVER's — they are what the engine ranked the
 * stored cards as, and re-deriving them here would open the door to the screen disagreeing with
 * the payout. `key` (the tie-break vector) is not published because nothing on screen reads it;
 * it is filled with an empty array rather than invented, so a future reader cannot mistake a
 * placeholder for a comparison the server made.
 */
export function toRank(r: SrvRank): HandRank | null {
  if (!r || typeof r !== 'object') return null;
  const cat = Number(r.cat);
  if (!Number.isInteger(cat) || cat < 1 || cat > 6) return null;
  const name = String(r.name || CATEGORY_NAME[cat as Category] || '');
  return { cat: cat as Category, key: [], name };
}

/**
 * How many of the six cards have been turned `elapsed` ms into the reveal.
 *
 * Card 1 lands the instant the reveal starts (`resolvesAt`) and each later card one
 * `TP_FLIP_MS` after the one before it, so the sixth is face up at `resolvesAt + 5 × 1100`.
 * The sixth card still owns its own 1100 ms beat before the verdict — see the header — which is
 * why this is capped at CARDS and the phase, not this function, decides when the reveal is over.
 */
export function flipCountAt(elapsedMs: number): number {
  if (!(elapsedMs >= 0)) return 0;
  return Math.min(CARDS, Math.floor(elapsedMs / FLIP_MS) + 1);
}

/** `?practice=1` → the Phase-A local clock (dev sandbox), never the money path. */
function practiceRequested(): boolean {
  try { return new URLSearchParams(window.location.search).get('practice') === '1'; }
  catch { return false; }
}

const uuid = (): string => {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* older webview */ }
  return `tp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * The clock
 * ════════════════════════════════════════════════════════════════════════════════════════ */

/** A chip on the table. `Bet` (side, amount, id) plus what the server needs to move it. */
type LiveBet = Bet & {
  /** false once the ack has landed */
  pending: boolean;
  /** the real `shared_round_bets.id`, null while pending */
  betId: number | null;
  /** the idempotency key this chip was bought with — one per tap, forever */
  key: string;
  roundNo: number;
  roundId: number | null;
};

export class ServerTableClock implements TableClock {
  private clock: ClockSync;
  private poller: Poller;
  private timer = 0;
  private subs = new Set<() => void>();
  private dead = false;

  /** The last `/state` we believe. Null until the first poll lands. */
  private state: TpState | null = null;
  private tableId: number | null = null;

  /** My chips, per round number — kept after the round settles so the result screen can still
   *  say what I staked (the server drops settled rounds out of `myBets`). */
  private betsByRound = new Map<number, LiveBet[]>();
  /** Chips whose ack has not landed but which the player has already taken back. */
  private cancelOnAck = new Set<string>();

  /** The server's balance, and the optimistic holds not yet reflected in it. */
  private serverWallet = 0;
  private walletKnown = false;
  private holds = new Map<string, number>();
  /** Bumped on every acked write, so a `/state` that was already in flight cannot rewind it. */
  private writeSeq = 0;

  /**
   * ONE money write at a time, in the order the player made them (2026-09-06).
   *
   * The Page's "one hand per round" rule turns a side switch into `unbet(); bet(total)` — every
   * chip taken back and the whole stake re-placed on the other hand. Fired concurrently, the
   * `/bet` can reach the server BEFORE the refunds commit, and the server (rightly) prices it
   * against the balance and the round total it can actually see: a player with ₹1,000 who has
   * ₹500 on PLAYER A and taps PLAYER B is asking to stake ₹600 out of the ₹500 the server still
   * holds, and gets `insufficient` — or, on a bigger stake, `bad_amount` for passing
   * `max_bet_per_user_round`, because the chips being cancelled still count. The chip then
   * vanishes off the table after the UI has already said MOVED ✓, for a reason the player was
   * never shown.
   *
   * Serialising the writes removes the race at its source: each `/unbet` has COMMITTED (its
   * response is only sent after the transaction) before the follow-up `/bet` is issued, so the
   * server sees exactly the table the player sees. It costs one round trip per cancelled chip —
   * tens of milliseconds against a lock cushion of 650 — and it cannot reorder anything, because
   * a chain is armed only when the previous write has finished. Nothing here retries: a refused
   * write is still handled by its own catch, and the chain moves on.
   */
  private writeChain: Promise<void> = Promise.resolve();

  private r: Round | null = null;
  private sig = '';
  private displayedNo: number | null = null;
  private syncingFlag = false;
  private lastErr: string | null = null;

  /**
   * @param tableSeed  the Phase-A local seed. Ignored on a real table: the fake crowd must be
   *                   a pure function of the SERVER's `(table_id, round_no, fake_seed)` so two
   *                   players at one table see one theatre (plan §5.4). Kept in the signature
   *                   so `Page.tsx` needs no change beyond its import.
   * @param wallet     the Page's balance sink — driven to the server's number, never trusted.
   */
  constructor(private tableSeed: number, private wallet: Wallet, _firstRoundNo = 1) {
    // Practice mode keeps the Phase-A clock alive for the dev sandbox (`?practice=1`): a
    // constructor may hand back another object, so the swap costs the Page nothing.
    if (practiceRequested()) {
      return new LocalTableClock(tableSeed, wallet, _firstRoundNo) as unknown as ServerTableClock;
    }

    this.clock = createClockSync('/teen_patti/time');
    this.clock.startAutoSync(30_000);

    this.poller = createPoller({
      poll: () => this.pollState(),
      now: () => performance.now(),
      edges: () => {
        const r = this.r;
        if (!r) return [];
        return [r.locksAt, r.revealStartsAt, r.settlesAt, r.nextOpensAt];
      },
      busy: () => this.syncingFlag || this.holds.size > 0,
      onError: (e) => { if (e instanceof ApiError) this.lastErr = e.message; },
    });
    this.poller.start();

    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  /* ── the TableClock interface ────────────────────────────────────────────────────────── */

  /** The Page renders in the `performance.now()` domain; every server stamp was converted on
   *  the way in (`toPerf`), so this stays exactly what `LocalTableClock` returned. */
  serverNow(): number { return performance.now(); }

  /**
   * The round in play.
   *
   * Null until the first `/state` lands — which `Page.tsx` already handles everywhere
   * (`r?.no`, `if (!clock || !r) return`, `r?.shownA ?? []`). A placeholder round would mean a
   * fabricated countdown on screen, so we return nothing rather than something untrue; the
   * cast is to the interface's non-null type, which the Page guards.
   */
  round(): Round {
    this.recompute();
    return this.r as Round;
  }

  myBets(): Bet[] {
    const no = this.displayedNo;
    if (no == null) return [];
    return this.betsByRound.get(no) ?? [];
  }

  history(): { winner: Side | 'push'; catA: number; catB: number }[] {
    const rows = this.state?.history ?? [];
    const out: { winner: Side | 'push'; catA: number; catB: number }[] = [];
    for (const h of rows) {
      const res = h.result;
      if (!res) continue;                        // an idle round settled with nothing to show
      const w = res.winner;
      if (w !== 'a' && w !== 'b' && w !== 'push') continue;
      const ra = toRank(res.ra); const rb = toRank(res.rb);
      if (!ra || !rb) continue;
      out.push({ winner: w, catA: ra.cat, catB: rb.cat });
    }
    return out;                                  // newest first, as the local clock kept it
  }

  /**
   * What this round did to me. The server's `shared_user_ledger` row is the truth and is
   * preferred the instant it exists; until settlement lands we show the outcome DERIVED from
   * the server's own decided result (`betReturn` — literally the same function the settle tx
   * runs, parity-gated against `teen_patti/engine.js`), so a winner never flashes "YOU SAT OUT"
   * in the gap between the reveal and the credit. Nothing here is shown before the server has
   * revealed the result.
   */
  myOutcome(): { staked: number; returned: number; net: number } | null {
    const r = this.r;
    if (!r || (r.status !== 'result' && r.status !== 'intermission')) return null;
    const srv = this.settledOutcomeFor(this.displayedNo);
    if (srv) return srv;
    return this.provisionalOutcome();
  }

  subscribe(cb: () => void) { this.subs.add(cb); return () => { this.subs.delete(cb); }; }

  /** The server has told us the balance at least once (first `/state`, or a write ack). */
  walletReady() { return this.walletKnown; }

  /**
   * Put a chip down. Synchronous, like the local clock: the answer is the LOCAL guard's
   * (window closed / not enough money); the server's answer arrives on the ack and either
   * confirms the chip or takes it back off the table.
   */
  bet(side: Side, amount: number): { ok: true } | { ok: false; reason: 'locked' | 'balance' } {
    this.recompute();                             // decide against the phase as it is RIGHT NOW
    const r = this.r;
    const st = this.state;
    if (!r || !st || !this.bettable(r)) return { ok: false as const, reason: 'locked' as const };
    const amt = Math.floor(Number(amount));
    if (!(amt > 0)) return { ok: false as const, reason: 'balance' as const };
    if (amt > this.displayBalance()) return { ok: false as const, reason: 'balance' as const };

    const key = uuid();
    const b: LiveBet = {
      side, amount: amt, id: `p:${key}`,
      pending: true, betId: null, key,
      roundNo: r.no, roundId: this.displayedRoundId(),
    };
    this.addBet(b);
    this.holds.set(key, -amt);                    // optimistic: the chip has left the wallet
    this.pushWallet();
    this.emit();
    this.enqueue(() => this.sendBet(b));
    return { ok: true as const };
  }

  /**
   * Take every chip of this round back (the Page's UNDO, and the first half of a side switch).
   * `/unbet` is per BET (contract C0.3), so this fans out — one call per chip, each idempotent
   * on its own bet id. Returns the amount taken back, optimistically; a refusal puts the chip
   * back exactly where it was.
   */
  unbet(): number {
    this.recompute();
    const r = this.r;
    if (!r || !this.bettable(r)) return 0;
    const no = this.displayedNo;
    if (no == null) return 0;
    const list = this.betsByRound.get(no) ?? [];
    if (!list.length) return 0;

    const total = list.reduce((s, b) => s + b.amount, 0);
    this.betsByRound.set(no, []);
    for (const b of list) {
      if (b.betId != null) {
        this.holds.set(`u:${b.key}`, b.amount);   // optimistic: the chip is on its way back
        this.enqueue(() => this.sendUnbet(b));
      } else {
        // Still in flight — cancel it the moment we learn its id.
        this.cancelOnAck.add(b.key);
      }
    }
    this.pushWallet();
    this.emit();
    return total;
  }

  destroy() {
    this.dead = true;
    window.clearInterval(this.timer);
    try { this.poller?.stop(); } catch { /* already stopped */ }
    try { this.clock?.destroy(); } catch { /* already stopped */ }
    this.subs.clear();
    // The game owned the balance while it was on screen; hand the rest of the app the truth.
    void refreshWallet();
  }

  /* ── extras (not part of the interface; safe for a later UI to read) ─────────────────── */

  /** True when the deal is overdue: past `resolvesAt + 1 s` with no result from the server.
   *  A UI may show SYNCING…; this clock simply refuses to invent a card. */
  syncing(): boolean { return this.syncingFlag; }
  /** The last error message the server gave us (bet refused, limit hit). */
  lastError(): string | null { return this.lastErr; }
  /** The server's bet bounds for this game — the Page's hardcoded caps should come from here. */
  limits(): { min: number; max: number; presets: number[]; maxPerRound: number } | null {
    return this.state?.bet ?? null;
  }
  /** The live table (id, name, pace) once known. */
  table(): TpState['table'] | null { return this.state?.table ?? null; }
  /** Server epoch ms, for anything that needs the wall clock rather than the perf domain. */
  serverEpochNow(): number { return this.clock.serverNow(); }
  isSynced(): boolean { return this.clock.isSynced(); }

  /* ── polling ─────────────────────────────────────────────────────────────────────────── */

  private async pollState(): Promise<void> {
    const seqAtStart = this.writeSeq;
    const t0 = performance.now();
    const path = this.tableId == null ? '/teen_patti/state' : `/teen_patti/state?table=${this.tableId}`;
    const st = await api.get<TpState>(path);
    const t1 = performance.now();
    if (this.dead) return;

    // A server timestamp we can anchor on before the first `/time` burst completes.
    this.clock.noteFallback(Number(st.serverNow), t0, t1);

    this.state = st;
    this.tableId = Number(st.table?.id) || this.tableId;
    this.lastErr = null;

    // A poll is only fresher than the last write if no write acked while it was in flight:
    // the ack carries the POST-write balance and bet list, this read may predate both. When a
    // write did land, keep what the ack told us and go round again immediately.
    if (seqAtStart === this.writeSeq) {
      this.serverWallet = Number(st.wallet) || 0;
      this.walletKnown = true;
      this.mergeServerBets(st);
    } else {
      this.poller.kick();
    }
    this.pushWallet();
    this.tick();                                  // re-derive + emit on the fresh truth
  }

  /**
   * Reconcile my chips with the server's. The server is authoritative for every round it
   * still reports (`round` + `nextRound`): a chip it does not list was cancelled or never
   * landed. Chips still pending (no ack yet) are kept — the server has not seen them.
   */
  private mergeServerBets(st: TpState) {
    const live = new Set<number>();
    if (st.round) live.add(st.round.no);
    if (st.nextRound) live.add(st.nextRound.no);

    const byRound = new Map<number, LiveBet[]>();
    for (const mb of st.myBets || []) {
      const side = mb.market === 'a' || mb.market === 'b' ? (mb.market as Side) : null;
      if (!side) continue;
      const arr = byRound.get(mb.roundNo) ?? [];
      arr.push({
        side, amount: Number(mb.amount), id: String(mb.betId),
        pending: false, betId: Number(mb.betId), key: `srv:${mb.betId}`,
        roundNo: Number(mb.roundNo), roundId: Number(mb.roundId),
      });
      byRound.set(mb.roundNo, arr);
    }

    for (const no of live) {
      const fromServer = byRound.get(no) ?? [];
      const mine = this.betsByRound.get(no) ?? [];
      const stillPending = mine.filter((b) => b.pending && !this.cancelOnAck.has(b.key));
      // A chip the player has just taken back must not reappear because this `/state` was
      // read before the refund committed.
      const cancelling = new Set<string>();
      for (const k of this.holds.keys()) if (k.startsWith('u:')) cancelling.add(k.slice(2));
      const kept = fromServer.filter((b) => {
        const prev = mine.find((m) => m.betId === b.betId);
        return !(prev && cancelling.has(prev.key));
      });
      this.betsByRound.set(no, [...kept, ...stillPending]);
    }

    // Forget rounds that are neither live nor on screen (a long session must not grow).
    for (const no of [...this.betsByRound.keys()]) {
      if (!live.has(no) && no !== this.displayedNo && no !== (st.lastResult?.no ?? -1)) {
        this.betsByRound.delete(no);
      }
    }
  }

  /* ── writes ──────────────────────────────────────────────────────────────────────────── */

  /** Put one money write at the back of the queue. See `writeChain`. */
  private enqueue(task: () => Promise<void>) {
    this.writeChain = this.writeChain.then(task, task).catch(() => { /* handled in the task */ });
  }

  private async sendBet(b: LiveBet) {
    try {
      const ack = await api.post<BetAck>('/teen_patti/bet', {
        table: this.tableId ?? undefined,
        roundId: b.roundId ?? undefined,
        roundNo: b.roundNo,
        market: b.side,
        amount: b.amount,
        idempotencyKey: b.key,
        // Teen Patti never pre-bets the next round: a chip on PLAYER A is priced against THIS
        // round's deal and would be silently re-priced against a different one (plan §5.3).
        // `adapter.supportsAllowNext` is false, so the server would refuse it anyway; sending
        // false says so out loud.
        allowNext: false,
      });
      if (this.dead) return;
      this.writeSeq += 1;
      this.holds.delete(b.key);
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;

      const list = this.betsByRound.get(b.roundNo) ?? [];
      const idx = list.findIndex((x) => x.key === b.key);
      const settledBet: LiveBet = {
        ...b, pending: false, betId: Number(ack.betId), id: String(ack.betId),
        roundId: Number(ack.roundId), roundNo: Number(ack.roundNo),
      };
      if (this.cancelOnAck.has(b.key)) {
        // UNDO landed while this tap was in flight: take it straight back off the table.
        this.cancelOnAck.delete(b.key);
        if (idx >= 0) list.splice(idx, 1);
        this.holds.set(`u:${b.key}`, b.amount);
        this.enqueue(() => this.sendUnbet(settledBet));
      } else if (idx >= 0) {
        list[idx] = settledBet;
        // The server may have placed it on the round we asked for and no other; if the round
        // number came back different, move the chip to where the money actually is.
        if (settledBet.roundNo !== b.roundNo) {
          list.splice(idx, 1);
          const dst = this.betsByRound.get(settledBet.roundNo) ?? [];
          dst.push(settledBet);
          this.betsByRound.set(settledBet.roundNo, dst);
        }
      }
      this.betsByRound.set(b.roundNo, list);
      this.pushWallet();
      this.emit();
      this.poller.kick();
    } catch (e) {
      if (this.dead) return;
      // Refused: the money never left. Remove the chip and hand the hold back.
      this.holds.delete(b.key);
      this.cancelOnAck.delete(b.key);
      const list = this.betsByRound.get(b.roundNo) ?? [];
      const idx = list.findIndex((x) => x.key === b.key);
      if (idx >= 0) list.splice(idx, 1);
      this.betsByRound.set(b.roundNo, list);
      this.lastErr = e instanceof ApiError ? e.message : 'That bet did not go through.';
      this.pushWallet();
      this.emit();
      this.poller.kick();
    }
  }

  private async sendUnbet(b: LiveBet) {
    const holdKey = `u:${b.key}`;
    try {
      const ack = await api.post<UnbetAck>('/teen_patti/unbet', {
        betId: b.betId, idempotencyKey: `${b.key}:undo`, reason: 'undo',
      });
      if (this.dead) return;
      this.writeSeq += 1;
      this.holds.delete(holdKey);
      this.serverWallet = Number(ack.wallet) || 0;
      this.walletKnown = true;
      this.pushWallet();
      this.emit();
      this.poller.kick();
    } catch (e) {
      if (this.dead) return;
      // Too late (or any refusal): the chip is STILL on the table and still at risk. Put it
      // back on screen rather than showing money the player does not have.
      this.holds.delete(holdKey);
      const list = this.betsByRound.get(b.roundNo) ?? [];
      if (!list.some((x) => x.betId === b.betId)) list.push({ ...b, pending: false });
      this.betsByRound.set(b.roundNo, list);
      this.lastErr = e instanceof ApiError ? e.message : 'That chip could not be taken back.';
      this.pushWallet();
      this.emit();
      this.poller.kick();
    }
  }

  /* ── wallet ──────────────────────────────────────────────────────────────────────────── */

  private displayBalance(): number {
    let d = 0;
    for (const v of this.holds.values()) d += v;
    return Math.max(0, (this.walletKnown ? this.serverWallet : this.wallet.balance()) + d);
  }

  /** Drive the Page's balance sink to the number we believe, using only the `Wallet` seam. */
  private pushWallet() {
    if (!this.walletKnown) return;
    const target = this.displayBalance();
    const cur = this.wallet.balance();
    const d = target - cur;
    if (d > 0) this.wallet.credit(d);
    else if (d < 0) this.wallet.debit(-d);
    setWalletBalance(this.serverWallet);          // the app-wide chip shows the SERVER balance
  }

  /* ── derivation ──────────────────────────────────────────────────────────────────────── */

  private addBet(b: LiveBet) {
    const list = this.betsByRound.get(b.roundNo) ?? [];
    list.push(b);
    this.betsByRound.set(b.roundNo, list);
  }

  private displayedRoundId(): number | null {
    const st = this.state; const no = this.displayedNo;
    if (!st || no == null) return null;
    for (const c of [st.round, st.nextRound, st.lastResult]) if (c && c.no === no) return c.id;
    return null;
  }

  /** Bets may only be placed while the window is genuinely open on the server's clock. */
  private bettable(r: Round): boolean {
    if (r.status !== 'betting') return false;
    return performance.now() < r.locksAt - LOCK_CUSHION_MS;
  }

  private settledOutcomeFor(no: number | null): { staked: number; returned: number; net: number } | null {
    const lr = this.state?.lastResult;
    if (!lr || no == null || lr.no !== no) return null;
    const o = lr.myOutcome;
    if (!o) return null;
    return { staked: Number(o.staked), returned: Number(o.returned), net: Number(o.net) };
  }

  /**
   * `betReturn` on the server's decided winner — the settle tx's own arithmetic, including the
   * PUSH, which pays back exactly the stake (X1.00) to BOTH sides rather than refunding it.
   */
  private provisionalOutcome(): { staked: number; returned: number; net: number } | null {
    const r = this.r;
    if (!r || !r.winner) return null;
    const bets = this.myBets();
    if (!bets.length) return null;
    let staked = 0; let returned = 0;
    for (const b of bets) {
      staked += b.amount;
      returned += betReturn(b.amount, b.side, r.winner);
    }
    return { staked, returned, net: returned - staked };
  }

  /**
   * Choose the round on screen and derive its phase from the server's stamps.
   *
   * Everything here is a pure function of (`this.state`, now). No timers advance it, no local
   * randomness feeds it: if the server has not decided, the phase simply does not advance.
   */
  private recompute(): string {
    const st = this.state;
    if (!st) { this.r = null; this.displayedNo = null; return ''; }
    const now = performance.now();
    const toPerf = (ms: number | null | undefined) => (ms == null ? null : this.clock.toPerf(Number(ms)));

    // Candidates, newest first: the round in play, the one after it, and the one just settled.
    // A `void` round (outage void, §5.2) is never shown — there is nothing true to draw for it.
    const cands = [st.nextRound, st.round, st.lastResult]
      .filter((c): c is SrvRound => !!c && c.status !== 'void' && c.opensAt != null)
      .sort((a, b) => b.no - a.no);
    if (!cands.length) { this.r = null; this.displayedNo = null; return ''; }

    // The newest round whose window has actually opened; before the first one opens, the
    // earliest known round (so the countdown is on screen at boot rather than a blank table).
    const src = cands.find((c) => {
      const o = toPerf(c.opensAt);
      return o != null && o <= now;
    }) || cands[cands.length - 1];

    const opensAt = toPerf(src.opensAt) ?? now;
    const locksAt = toPerf(src.locksAt) ?? (opensAt + Number(st.table?.windowMs || 15_000));
    const revealStartsAt = toPerf(src.resolvesAt) ?? (locksAt + 800);
    const settlesAt = toPerf(src.settlesAt);
    const nextOpensAt = toPerf(src.nextOpensAt);

    // A deal is usable only if it is COMPLETE: a winner we recognise, three valid cards on each
    // side and both ranks. A truncated or malformed hand would put the wrong card in a fan and
    // light the wrong rung on the ladder, so a partial deal counts as no deal and the table
    // waits instead.
    const res = src.result;
    const aCards: Card[] = (res && Array.isArray(res.a) ? res.a : []).map(toCard).filter((c): c is Card => c != null);
    const bCards: Card[] = (res && Array.isArray(res.b) ? res.b : []).map(toCard).filter((c): c is Card => c != null);
    const ra = toRank(res?.ra ?? null);
    const rb = toRank(res?.rb ?? null);
    const w = res?.winner;
    const hasDeal = !!res && (w === 'a' || w === 'b' || w === 'push')
      && aCards.length === 3 && bCards.length === 3 && !!ra && !!rb;

    // ── phase ─────────────────────────────────────────────────────────────────────────
    // The ONLY way past 'locked' is a deal the server has already written. Missing it past
    // `resolvesAt + 1 s` is a sync problem, not a licence to turn a card.
    let status: RoundStatus;
    let shownA: Card[] = [];
    let shownB: Card[] = [];
    this.syncingFlag = false;

    if (now < locksAt) {
      status = 'betting';
    } else if (!hasDeal) {
      status = 'locked';
      this.syncingFlag = now > revealStartsAt + SYNCING_AFTER_MS;
    } else if (now < revealStartsAt) {
      status = 'locked';
    } else {
      // Six flips own `CARDS × FLIP_MS`; the result hold owns whatever is left up to the
      // server's `settles_at` (1500 ms when the worker's timeline is intact).
      const revealEndsAt = revealStartsAt + CARDS * FLIP_MS;
      const endsAt = settlesAt ?? (revealEndsAt + RESULT_MS);
      if (now < revealEndsAt) {
        status = 'revealing';
        const n = flipCountAt(now - revealStartsAt);
        // Dealt alternately A, B, A, B, A, B — the odd cards are PLAYER A's.
        shownA = aCards.slice(0, Math.ceil(n / 2));
        shownB = bCards.slice(0, Math.floor(n / 2));
      } else if (now < endsAt) {
        status = 'result';
        shownA = aCards; shownB = bCards;
      } else {
        // Past `settles_at`: intermission until the next round opens — and PAST `nextOpensAt`
        // too, because a `pending` row is not worth switching to until its window has actually
        // begun. Holding on the finished round beats blanking the table.
        status = 'intermission';
        shownA = aCards; shownB = bCards;
      }
    }

    const isDone = status === 'result' || status === 'intermission';
    const winner: Side | 'push' | null = isDone && hasDeal ? (w as Side | 'push') : null;

    this.displayedNo = src.no;
    this.r = {
      no: src.no,
      status,
      opensAt,
      locksAt,
      revealStartsAt,
      shownA,
      shownB,
      winner,
      rankA: isDone && hasDeal ? ra : null,
      rankB: isDone && hasDeal ? rb : null,
      settlesAt,
      nextOpensAt,
      // §5.4: the theatre must be identical on every device at this table, so it is seeded
      // from the SERVER's table seed and round number — never a per-device localStorage seed.
      fakeSeed: mix(Number(st.table?.fakeSeed ?? this.tableSeed) >>> 0, src.no),
    };

    const bets = this.betsByRound.get(src.no) ?? [];
    const betSig = bets.map((b) => `${b.id}${b.side}${b.amount}${b.pending ? 'p' : ''}`).join(',');
    const out = this.myOutcomeSig();
    const shown = shownA.length + shownB.length;
    return `${src.no}|${status}|${shown}|${betSig}|${this.displayBalance()}|${out}|${this.syncingFlag ? 's' : ''}`;
  }

  private myOutcomeSig(): string {
    const o = this.settledOutcomeFor(this.displayedNo);
    return o ? `${o.staked}/${o.returned}/${o.net}` : '';
  }

  private tick() {
    if (this.dead) return;
    const sig = this.recompute();
    if (sig === this.sig) return;
    this.sig = sig;
    this.emit();
  }

  private emit() { this.subs.forEach((cb) => { try { cb(); } catch { /* a bad subscriber must not stop the table */ } }); }
}

export default ServerTableClock;
